// ─────────────────────────────────────────────────────────────────
// Glass-i — Goods Receipt Note (GRN) API client (Stage 6P.3).
//
// Thin axios wrappers around:
//   /api/erp/purchase/grns/*  (Goods Receipt Notes — 6P.3)
//
// Mirrors the purchaseApi shape — the `unwrap` helper handles
// both the {data:{data:...}} envelope used by most list/create
// endpoints and the bare {data:...} shape used by a handful of
// helper routes (preview, po balance lookups).
//
// Backend is the source of truth for ALL receipt math:
//   received_sqmt, received_sqft, received_total_length,
//   rejected_sqmt, rejected_sqft, accepted/rejected balances,
//   PO pending balance.
// Frontend mirrors for instant UX but never wins.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const GRN_BASE = '/api/erp/purchase/grns';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const goodsReceiptsApi = {
  list:                 (params = {}) => client.get(GRN_BASE, { params }).then(unwrap),
  get:                  (id)          => client.get(`${GRN_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create:               (payload)     => client.post(GRN_BASE, payload).then(unwrap),
  update:               (id, payload) => client.put(`${GRN_BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove:               (id)          => client.delete(`${GRN_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  receive:              (id, payload) =>
    client.post(`${GRN_BASE}/${encodeURIComponent(id)}/receive`, payload).then(unwrap),
  inspect:              (id, payload) =>
    client.post(`${GRN_BASE}/${encodeURIComponent(id)}/inspect`, payload).then(unwrap),
  accept:               (id)          =>
    client.post(`${GRN_BASE}/${encodeURIComponent(id)}/accept`).then(unwrap),
  cancel:               (id, { reason } = {}) =>
    client.post(`${GRN_BASE}/${encodeURIComponent(id)}/cancel`, { reason }).then(unwrap),
  preview:              (payload)     =>
    client.post(`${GRN_BASE}/preview`, payload).then(unwrap),
  createFromPo:         (poId, payload) =>
    client.post(`${GRN_BASE}/from-po/${encodeURIComponent(poId)}`, payload).then(unwrap),
  getPoReceiptBalance:  (poId)        =>
    // Backend route is GET /grns/po-balance/:poId (erpGoodsReceipts.js).
    client.get(`${GRN_BASE}/po-balance/${encodeURIComponent(poId)}`).then(unwrap),
  // GRN Receipt print — returns a self-contained HTML document for the
  // in-app iframe viewer. `responseType: 'text'` + identity transform so
  // axios hands back the raw HTML string (never JSON-parsed).
  viewReport:           (id)          =>
    client
      .get(`${GRN_BASE}/${encodeURIComponent(id)}/report`, {
        responseType: 'text',
        headers: { Accept: 'text/html' },
        transformResponse: [(d) => d]
      })
      .then((resp) => resp.data)
};

export default { goodsReceiptsApi };
